"use client";

/**
 * Per-target setTimeout id for the `data-flash` removal. Keyed by the
 * target element so rapid repeated activations of the same citation don't
 * race — the previous removal-timer is cleared before scheduling a new
 * one. Using a WeakMap means entries vanish if the target element is
 * garbage-collected (route change unmounts the sidenote), so no
 * lifecycle wiring is needed in the components themselves.
 */
const flashTimers = new WeakMap<Element, number>();

const FLASH_DURATION_MS = 1400;

/**
 * Apply the citation-arrival pulse to a sidenote entry. Used by the
 * `<Cite>` click handler and by `<SourceSidenote>`'s hash-navigation
 * effect so both code paths behave identically — including under
 * `prefers-reduced-motion`, where the visible highlight is the
 * `[data-flash="true"]` wash (transient) rather than `:target`
 * (which would persist as long as the URL hash matches).
 *
 * `scroll: true` brings the target into the centre of the viewport,
 * smooth-scrolled unless the user prefers reduced motion. `focus` is
 * always called (with `preventScroll`) so screen readers announce the
 * entry.
 */
export function flashCite(
  target: HTMLElement,
  opts: { scroll?: boolean } = {},
): void {
  if (opts.scroll) {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    target.scrollIntoView({
      behavior: reduced ? "auto" : "smooth",
      block: "center",
    });
  }
  target.focus({ preventScroll: true });

  // Clear any in-flight removal timer for this target before re-arming
  // — without this, rapid same-target clicks would let an earlier timer
  // strip `data-flash` partway through a later pulse.
  const prev = flashTimers.get(target);
  if (prev !== undefined) {
    window.clearTimeout(prev);
    flashTimers.delete(target);
  }
  target.removeAttribute("data-flash");
  // Toggle the attribute back on across a frame so CSS sees a state
  // change and restarts the animation; setting the same attribute
  // value twice in a row otherwise wouldn't.
  requestAnimationFrame(() => {
    target.setAttribute("data-flash", "true");
    const id = window.setTimeout(() => {
      target.removeAttribute("data-flash");
      flashTimers.delete(target);
    }, FLASH_DURATION_MS);
    flashTimers.set(target, id);
  });
}
